<template>
  <section class="activity-stage">
    <div id="curtain" :style="stageStyle">
      <div id="stage">
        <figure v-for="(musician, index) in currentRound.musicians" :key="`musician${index}`">
          <figcaption v-show="musician.name" v-text="musician.name"></figcaption>
          <img :src="musician.img" alt="musician" />
        </figure>
        <figure>
          <figcaption id="primary">Primary</figcaption>
          <img
            id="color"
            v-if="completed && currentRound.primary"
            :src="currentRound.primary.color"
            alt="musician"
          />
          <img
            id="bw"
            v-if="!completed && currentRound.primary"
            :src="currentRound.primary.img"
            alt="musician"
          />
        </figure>
        <div id="dialog">
          <text-display-list id="input" text-size="small">
            <blinking-cursor-text
              v-for="step in currentRound.steps"
              :key="`step${step.stepIndex}`"
              :text="step.answer"
              :text-step="step.stepIndex"
            ></blinking-cursor-text>
          </text-display-list>
        </div>
      </div>
    </div>
    <slot name="keyboard"></slot>
  </section>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';

import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';

import stage from 'assets/images/activities/musicians-on-stage/stage.png';

export default {
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],
  components: {
    BlinkingCursorText,
    TextDisplayList
  },
  data() {
    return {
      activityName: 'MusiciansOnStage',
      sfx: null,
      stageStyle: {
        backgroundImage: `url(${stage})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right top',
        backgroundSize: 'cover'
      },
      completed: false
    };
  },

  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();
      this.createRounds(roundSetup);
      await this.loadImages();
      this.sfx = await this.loadSounds();
    },

    async loadImages() {
      this.queueImage(this.stage);

      this.rounds.forEach(r => {
        this.queueImage(r.primary.img);
        this.queueImage(r.primary.color);
        r.musicians.forEach(m => {
          this.queueImage(m.img);
        });
      });

      return this.downloadImages();
    },

    async loadSounds() {
      this.rounds.forEach(({ audio }) => {
        if (audio) this.queueAudio(audio);
      });

      const cache = await this.downloadAudio();

      return this.rounds.map(({ audio }) => cache.get(audio));
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds,
        instructions: {
          written: 'Type the name and facts.',
          voice: `${this.voiceInstructionsPrefix}/onthestage-${this.getRandomVoice()}-1.mp3`,
          delay: 3
        }
      };
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;
      return {
        keyboardType,
        roofs
      };
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();
      this.$nextTick(() => {
        const instructions = document.querySelector('.activity-instructions');
        instructions.className += ' white-instructions';
        TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
      });
    },

    handleCorrectKey() {
      this.sndType();
      this.incrementTypingIndex();

      if (this.isEndOfCurrentSentence) {
        this.setCurrentStep();
      }

      if (this.isEndOfRound) {
        this.handleRoundEnd();
      }
    },

    handleRoundEnd() {
      const timeline = new TimelineLite();
      const { roundIndex } = this.progress;
      const music = this.sfx[roundIndex];

      this.sndBell();
      this.completed = true;
      this.hideKeyboard();

      timeline.delay(0.8).call(() => {
        if (this.isLastRound) {
          music.play();
          music.on('end', () => {
            TweenMax.delayedCall(1.5, this.completeActivity);
          });
        } else {
          timeline
            .delay(2.5)
            .to('.activity-stage', 0.5, { opacity: 0 })
            .call(this.$nextTick, [() => timeline.to('.activity-stage', 0.5, { opacity: 1 })]);
        }
      });
    }
  }
};
</script>

<style lang="scss">
.white-instructions {
  color: #fff;
}

@media only screen and (min-device-width: 481px) and (max-device-width: 1024px) and (orientation: landscape) {
  .white-instructions {
    top: -12px !important;
  }
}
</style>
<style lang="scss" scoped>
.activity-stage {
  background-color: black;
  color: #fff;
  #curtain {
    width: 100%;
    height: 100%;
    margin-top: -8.5%;
    #stage {
      font-family: hwtfutura;
      font-weight: 400;
      font-style: normal;
      position: relative;
      display: inline-flex;
      margin: 15.5% 0 0 12%;
      figure {
        margin: 0;
        text-align: center;
        #primary {
          margin-top: 2%;
          color: transparent;
        }
      }
      #dialog {
        display: block;
        width: 260px;
        #input {
          position: relative;
          margin: 30% 0 0 8%;
          padding: 3.5%;
          background-color: #fff;
          line-height: 2;
          border-radius: 10px;
          &:before {
            content: '';
            position: absolute;
            display: block;
            margin: 10% 0 0 -20%;
            border-style: solid;
            border-width: 15px 50px 15px 0;
            border-color: transparent #fff;
          }
        }
      }
    }
  }
}

@media only screen and (min-device-width: 481px) and (max-device-width: 1024px) and (orientation: landscape) {
  .white-instructions {
    top: -12px !important;
  }
  .activity-stage {
    #curtain {
      margin-top: -16.5%;
      #stage {
        margin-top: 18.8%;
      }
    }
  }
}
</style>
